import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { AppView, NavigationHeader } from 'design-system';
import Choices from 'design-system/Choices';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { useFetchOld } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { useConnectedAccount } from 'features/invest/hooks/selectors';
import { editSavingsPlan, savePotsOnboardingState } from 'features/pots/actions';
import useNavigateToNextScreenForPotsOnboarding from 'features/pots/hooks/useNavigateToNextScreenForPotsOnboarding';
import LeaveOnboardingButton from 'features/wealth/components/LeaveOnboardingButton';
import { OnboardingFlow } from 'utils/types/navigation';
const options = ['Yes', 'No'];
const fetchOptions = {
    throws: false,
    muteError: true,
    muteResult: true,
    muteFetching: true,
};
const AutosaveOverdraftSettings = ({ route, navigation }) => {
    const colors = useColors();
    const dispatch = useAppDispatch();
    const mountedOnAnotherScreen = route.params && 'mountedOnAnotherScreen' in route.params ? route.params.mountedOnAnotherScreen : false;
    const { paddingHorizontalStyle } = useAppFrameDimensions(!!mountedOnAnotherScreen);
    const connectedAccount = useConnectedAccount();
    const contentContainerStyle = useMarginBottom('paddingBottom', undefined, paddingHorizontalStyle);
    const [, , , fetch] = useFetchOld(fetchOptions);
    const savingsPlanId = useAppSelector((state) => state.pots.savingsPlan?.id);
    const savingsPlanOverdraft = useAppSelector((state) => state.pots.savingsPlan?.allowOverdraft);
    const isOnboardingPots = useAppSelector((state) => !!state.pots.onboarding);
    const scrollY = useSharedValue(0);
    const navigateToNextScreen = useNavigateToNextScreenForPotsOnboarding();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const onSelect = useCallback((item) => {
        const allowOverdraft = item === 'Yes';
        if (isOnboardingPots) {
            dispatch(savePotsOnboardingState({ allowOverdraft }));
            navigateToNextScreen();
            return;
        }
        if (route.params && 'distribution' in route.params) {
            // Create flow
            if (connectedAccount) {
                navigation.navigate('AutosaveSelectBank', {
                    overdraft: allowOverdraft,
                    rules: route.params.rules,
                    distribution: route.params.distribution,
                    collectionFrequency: route.params.collectionFrequency,
                });
            }
            else {
                navigation.navigate('SelectFundingAccount', {
                    overdraft: allowOverdraft,
                    rules: route.params.rules,
                    distribution: route.params.distribution,
                    collectionFrequency: route.params.collectionFrequency,
                });
            }
        }
        else {
            // Edit flow
            if (savingsPlanId && allowOverdraft !== savingsPlanOverdraft) {
                fetch(editSavingsPlan(savingsPlanId, { allowOverdraft }));
            }
            navigation.goBack();
        }
    }, [connectedAccount, isOnboardingPots, navigateToNextScreen, route.params, savingsPlanId, savingsPlanOverdraft]);
    const outputColors = useMemo(() => [colors.background.dark, colors.cards.onDark], [colors.background.dark, colors.cards.onDark]);
    const scrollHandler = useAnimatedScrollHandler((event) => {
        scrollY.value = event.contentOffset.y;
    });
    return (<AppView type="secondary" withFrame={false}>
      <NavigationHeader scrollY={scrollY} regularTitleHeight outputColors={outputColors} isInTab={!!mountedOnAnotherScreen} leftContent={mountedOnAnotherScreen ? <LeaveOnboardingButton flow={OnboardingFlow.Pots}/> : undefined} rightContent={<View style={mountedOnAnotherScreen ? styles.rightContent : null}>
            <HelpScreenButton articlesKey={HELP_ARTICLES_KEY.AUTOSAVE_ONBOARDING}/>
          </View>}/>
      <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
        <ScreenInfoHeader title="Do you want to enable saving in overdraft?" subTitle="If you find it hard to break the reliance on your overdraft you can continue saving even if you’re in overdraft"/>
        <View style={styles.horizontalPadding}>
          <Choices data={options} onSelect={onSelect}/>
        </View>
      </Animated.ScrollView>
    </AppView>);
};
const styles = StyleSheet.create({
    horizontalPadding: {
        paddingHorizontal: rem(16),
    },
    rightContent: {
        marginRight: rem(16),
        marginBottom: rem(6),
        alignItems: 'flex-end',
    },
});
export default AutosaveOverdraftSettings;
